import { useEffect, useState } from "react"
import Table from "../../components/Table/Table"
import { Invoice, Transaction } from "../../models/transactions.model"
import {
  GridCallbackDetails,
  GridColDef,
  GridRowModel,
  GridRowParams,
  GridRowsProp,
  GridSortModel,
} from "@mui/x-data-grid"
import RefreshIcon from "@mui/icons-material/Refresh"
import TransactionDetail from "./TransactionDetail"
import { ListingRowMenu } from "../../components/ListingRowMenu.tsx"
import { allowedCategorisationRoles, MarkedStatus } from "../../constants/data"
import { selectLogin } from "../../slices/loginSlice"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import {
  ChipContainer,
  ExpandableChip,
  ReconcileIconButton,
  ReferenceNoCell,
  SyncIconButton,
} from "./styles"
import { formatPriceWithRupeeSymbol } from "../../utils/price-formatter"
import {
  getMarkedStatus,
  getTransactionDateDisplayValue,
  getTransactionTypeDisplayValue,
  shouldShowReconcileButton,
} from "../../utils/transactionHelpers"
import { MarkedStatusChip } from "./MarkedStatusChip"
import ReconciliationDrawer from "../ReconciliaionDrawer"
import {
  unCategoriseTransactionRequest,
  unMatchTransactionRequest,
} from "../../slices/reconcilationSlice"
import TransactionAction from "./TransactionAction"
import {
  resetRowSelectionModel,
  selectTransactions,
  setCategory,
  setRowSelectionModel,
  syncZohoData,
} from "../../slices/transactionsSlice"

interface TransactionTableProps {
  transactions: Transaction[]
  isLoading: boolean
  pageInfo: any
  paginationModel: any
  setPaginationModel: React.Dispatch<React.SetStateAction<any>>
  sortModel?: GridSortModel | undefined
  onSortModelChange?:
    | ((model: GridSortModel, details: GridCallbackDetails) => void)
    | undefined
  fetchTransaction: () => void
}

const CategoryRow = ({ row }: GridRowModel) => {
  const categories = row.categories
  const lastCategoryArray =
    categories && Object.values(categories).filter(Boolean)
  const lastCategoryId = lastCategoryArray && lastCategoryArray.pop()?.id
  const [expandedChip, setExpandedChip] = useState(lastCategoryId)

  const handleChipClick = (id: any) => {
    setExpandedChip(id)
  }

  return (
    <ChipContainer>
      {categories
        ? Object.keys(categories).map(
            (key) =>
              categories[key] && (
                <ExpandableChip
                  key={key}
                  label={
                    expandedChip === categories[key].id
                      ? categories[key].value
                      : ""
                  }
                  title={categories[key].value}
                  avatar={
                    <span>
                      {categories[key]?.value?.charAt(0)?.toUpperCase()}
                    </span>
                  }
                  isExpanded={expandedChip === categories[key].id}
                  customColor={
                    key === "level1"
                      ? "#fdb915"
                      : key === "level2"
                      ? "#6D5c3F"
                      : key === "level3"
                      ? "#36B37E"
                      : "default"
                  }
                  onClick={() => handleChipClick(categories[key].id)}
                />
              ),
          )
        : "NA"}
    </ChipContainer>
  )
}

export const TransactionTable = ({
  transactions,
  isLoading,
  pageInfo,
  paginationModel,
  setPaginationModel,
  sortModel,
  onSortModelChange,
  fetchTransaction,
}: TransactionTableProps) => {
  const { role } = useAppSelector(selectLogin)
  const dispatch = useAppDispatch()
  const [rowCountState, setRowCountState] = useState(
    pageInfo?.totalRowCount || 0,
  )
  const [dialogOpen, setDialogOpen] = useState(false)
  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false)

  const [rowIndex, setRowIndex] = useState<number>(0)

  const { rowSelectionModel, processScheduleData } =
    useAppSelector(selectTransactions)

  useEffect(() => {
    setRowCountState((prevRowCountState: number) =>
      pageInfo?.totalRowCount !== undefined
        ? pageInfo?.totalRowCount
        : prevRowCountState,
    )
  }, [pageInfo?.totalRowCount, setRowCountState])

  const handleDialogOpen = (index: number, id: string, categories?: any) => {
    categories &&
      dispatch(
        setCategory({
          category: categories.level1?.id,
          subCategory: categories.level2?.id,
          subCategory1: categories.level3?.id,
        }),
      )
    setRowIndex(index)
    setDialogOpen(true)
    dispatch(
      setRowSelectionModel({
        rowSelectionModel: [id],
      }),
    )
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
    dispatch(resetRowSelectionModel())
  }

  const handleReconcileDialogOpen = (index: number) => {
    setRowIndex(index)
    setReconcileDialogOpen(true)
  }

  const handleReconcileDialogClose = () => {
    setReconcileDialogOpen(false)
    handleDialogClose()
  }

  const handleSyncToZoho = (row: any) => {
    dispatch(syncZohoData(row.id))
  }

  const columns: GridColDef[] = [
    { field: "id", headerName: "ID", minWidth: 90, sortable: false },
    {
      field: "date",
      headerName: "Date",
      minWidth: 180,
      sortable: true,
      flex: 1,
    },
    {
      field: "referenceNo",
      headerName: "Reference No",
      minWidth: 120,
      flex: 1,
      sortable: false,
      renderCell: ({ value, row }) => {
        return (
          <ReferenceNoCell
            onClick={() =>
              handleDialogOpen(row.rowIndex, row.id, row.categories)
            }
          >
            {value || "--"}
          </ReferenceNoCell>
        )
      },
    },
    {
      field: "amount",
      headerName: "Amount",
      minWidth: 150,
      flex: 1,
      sortable: true,
      valueFormatter: ({ value }) => {
        return formatPriceWithRupeeSymbol(value)
      },
    },
    {
      field: "payee",
      headerName: "Payee",
      minWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <ListingRowMenu
            name="payee"
            options={
              row.payee
                ? [
                    {
                      value: row.zohoContactUrl,
                      name: row.payee,
                    },
                  ]
                : []
            }
          />
        )
      },
    },
    {
      field: "isMarked",
      headerName: "Marked",
      type: "boolean",
      minWidth: 100,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return <MarkedStatusChip row={row} />
      },
    },
    {
      field: "accountName",
      headerName: "Bank Name",
      minWidth: 180,
      flex: 1,
      sortable: false,
    },
    {
      field: "invoiceNumber",
      headerName: "Invoice Number",
      minWidth: 150,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <ListingRowMenu
            name="invoice"
            options={
              row?.settlements?.invoices?.length === 0
                ? []
                : row?.settlements?.invoices.map((invoice: Invoice) => {
                    return {
                      value: invoice.invoiceURL,
                      name: invoice.invoiceNumber,
                    }
                  })
            }
          />
        )
      },
    },
    {
      field: "type",
      headerName: "Type",
      minWidth: 80,
      flex: 1,
      sortable: false,
      valueFormatter: ({ value }) => {
        return getTransactionTypeDisplayValue(value)
      },
    },
    {
      field: "reconcile",
      headerName: "Actions",
      sortable: false,
      minWidth: 120,
      flex: 1,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        if (row.isCategorised || row.isReconciled || row.isMarked) {
          return (
            <TransactionAction
              title="Uncategorise"
              confirmText="Are you sure you want to uncategorise this transaction?"
              id={row.id}
              invoices={row?.settlements?.invoices}
              handleTransactionRequest={() =>
                dispatch(unCategoriseTransactionRequest(row.id))
              }
            />
          )
        }

        if (row.isMatched) {
          return (
            <TransactionAction
              title="Uncategorise" // using uncategorise for unmatch too
              confirmText="Are you sure you want to uncategorise this transaction?"
              id={row.id}
              handleTransactionRequest={() =>
                dispatch(unMatchTransactionRequest(row.id))
              }
            />
          )
        }

        return shouldShowReconcileButton(row) ? (
          <ReconcileIconButton
            onClick={() => handleReconcileDialogOpen(row.rowIndex)}
            disabled={processScheduleData.status && !row.isReconEnabled}
          >
            Reconcile
          </ReconcileIconButton>
        ) : null
      },
    },
    {
      field: "SyncToZoho",
      headerName: "Sync to Zoho",
      sortable: false,
      minWidth: 120,
      flex: 1,
      disableColumnMenu: true,
      renderCell: ({ row }) => {
        return (
          <SyncIconButton
            onClick={() => handleSyncToZoho(row)}
            disabled={
              getMarkedStatus(row.isMarked, row.isReconciled) !==
              MarkedStatus.PENDING
            }
          >
            <RefreshIcon />
          </SyncIconButton>
        )
      },
    },
    {
      field: "categories",
      headerName: "Tags",
      minWidth: 300,
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => <CategoryRow row={row} />,
    },
  ]

  const rows: GridRowsProp = transactions?.map((transaction, index) => ({
    rowIndex: index,
    id: transaction.id,
    date: transaction.date
      ? getTransactionDateDisplayValue(transaction.date)
      : "",
    description: transaction.description,
    amount: transaction.amount,
    type: transaction.type,
    referenceNo: transaction.referenceNo,
    valueDate: transaction.valueDate
      ? getTransactionDateDisplayValue(transaction.valueDate)
      : "",
    branch: transaction.branch,
    balance: transaction.balance,
    isCategorised: transaction.isCategorised,
    isMarked: transaction.isMarked,
    isMatched: transaction.isMatched,
    isReconciled: transaction.isReconciled,
    isReconEnabled: transaction.isReconEnabled,
    isSynced: transaction.isSynced,
    invoiceNumber: transaction.invoiceNumber,
    paymentId: transaction.paymentId,
    paymentURL: transaction.paymentURL,
    invoiceURL: transaction.invoiceURL,
    payee: transaction.payee,
    accountName: transaction.accountName,
    settlements: transaction.settlements,
    categories: transaction.categories,
    zohoTransactionId: transaction.zohoTransactionId,
    zohoContactUrl: transaction.zohoContactUrl,
    customerName: transaction.customerName,
    customerId: transaction.customerId,
  }))

  const columnVisibilityModel = {
    id: false,
    branch: false,
    balance: false,
    valueDate: false,
    paymentId: false,
  }

  return (
    <>
      <Table
        rows={rows}
        columns={columns}
        loading={isLoading}
        rowCount={rowCountState}
        pageSizeOptions={[10, 25, 50, 100]}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={setPaginationModel}
        disableColumnFilter
        columnVisibilityModel={columnVisibilityModel}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        checkboxSelection={allowedCategorisationRoles.includes(role)}
        disableRowSelectionOnClick
        onRowSelectionModelChange={(newRowSelectionModel) => {
          dispatch(
            setRowSelectionModel({
              rowSelectionModel: newRowSelectionModel as string[],
            }),
          )
        }}
        rowSelectionModel={rowSelectionModel}
        isRowSelectable={(params: GridRowParams) => !params.row.categories}
      />
      {dialogOpen && (
        <TransactionDetail
          open={dialogOpen}
          onClose={handleDialogClose}
          transactionData={transactions[rowIndex]}
          fetchTransaction={fetchTransaction}
        />
      )}
      {reconcileDialogOpen && (
        <ReconciliationDrawer
          open={reconcileDialogOpen}
          onClose={handleReconcileDialogClose}
          transactionData={transactions[rowIndex]}
        />
      )}
    </>
  )
}
